// POM-6199: one line item's section of the Split Order By Quantity dialog. Several line
// items can be split in a single submission, so each gets its own collapsible panel with
// its own split rows and its own totals — nothing here is shared between items.
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { addDays, startOfDay, subDays } from 'date-fns';
import PropTypes from 'prop-types';

import FillLaterAddressField from 'common/components/FillLaterAddressField';

import DateField from 'common/forms/DateField';
import TextField from 'common/forms/TextField';
import { validateQuantity } from 'common/forms/formValidations';
import { mutedLabel } from './SplitOrderByQuantityTiles';
import {
  QUANTITY_EPSILON,
  describeLineItem,
  fmtQuantity,
  isBlockedQuantityKey,
  parseQuantity,
  toNumber,
  totalToFillLater,
} from './splitOrderByQuantityHelpers';

// perfectRxTheme hides the native number spinners on every MuiInputBase, so they have to
// be put back for a field that is meant to be stepped with the arrows.
const NUMBER_SPINNER_SX = {
  '& input[type=number]': { MozAppearance: 'auto' },
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
    {
      WebkitAppearance: 'inner-spin-button',
      opacity: 1,
      height: 24,
      cursor: 'pointer',
    },
};

const SplitOrderByQuantityItemPanel = ({
  lineItem,
  index,
  splits,
  rowKeys,
  expanded,
  onToggle,
  onAddSplit,
  onRemoveSplit,
  totalError,
  earliestSelectableDate,
  latestSelectableDate,
  validateFillDate,
  isSingleItem,
  addresses,
  orderAddress,
  addressesLoading,
  addressDirectoryId,
  onAddressChange,
  onAddressBlockedChange,
  onAddressAcknowledgedChange,
}) => {
  const dispenseQuantity = toNumber(lineItem?.quantity);
  const later = totalToFillLater(splits);
  const fillsNow = dispenseQuantity - later;
  const noQuantityLeft = fillsNow < 2 - QUANTITY_EPSILON;
  const fieldPrefix = `items[${index}].splits`;

  // Once the splits exceed what can be dispensed the remainder goes negative, and "-120 now"
  // states a quantity the order could never hold. The count is dropped rather than clamped —
  // "0 now" would be just as untrue, since something always has to stay to fill now — leaving
  // the error icon beside it and the alert inside the panel to say what is wrong. This is the
  // same condition buildItemSummaryLine drops its footer line on.
  const headerTotals = [
    `${fmtQuantity(later)} to fill later`,
    fillsNow > 0 ? `${fmtQuantity(fillsNow)} now` : undefined,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Accordion
      expanded={expanded}
      onChange={onToggle}
      disableGutters
      data-testid={`split-item-panel-${lineItem?.lineItemId}`}
      sx={{
        mb: 1.5,
        border: (theme) =>
          `1px solid ${totalError ? theme.palette.error.main : theme.palette.divider}`,
        borderRadius: 1,
        '&:before': { display: 'none' },
        boxShadow: 'none',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`split-item-${index}-content`}
        id={`split-item-${index}-header`}
        // A quiet grey band, with the primary colour carried by the accent bar and the drug
        // name instead of by the fill. The patient banner above is a light primary tint, so
        // tinting this one too left the two reading as the same band whatever the strength;
        // changing the hue rather than the depth separates them without shouting.
        sx={{
          backgroundColor: 'grey.100',
          // Pulled one pixel out so the accent bar sits over the panel's own left border
          // rather than inside it: otherwise the thin grey line below the header starts a
          // pixel further left than the blue bar and the edge reads as stepped.
          ml: '-1px',
          borderLeft: (theme) => `5px solid ${theme.palette.primary.main}`,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          '& .MuiSvgIcon-root': { color: 'primary.main' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            {/* subtitle1 keeps h6's weight one size smaller, so an item header does not
                compete with the dialog's own heading. */}
            <Typography variant='subtitle1' fontWeight={500} color='primary.dark' noWrap>
              {describeLineItem(lineItem)}
            </Typography>
            {/* The NDC rides here with the Rx number rather than inside the panel: it
                identifies the item, so it belongs where the item is named — and it stays
                readable while the panel is collapsed. */}
            <Typography sx={{ color: mutedLabel, fontSize: '0.85em' }}>
              {[
                lineItem?.rxNumber && `RX ${lineItem.rxNumber}`,
                lineItem?.ndc && `NDC ${lineItem.ndc}`,
                `Prescribed qty ${lineItem?.prescribedQuantity ?? '—'}`,
                `Qty to dispense ${lineItem?.quantity ?? '—'}`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          </Box>

          {/* What this item contributes to the submission, readable while the panel is
              collapsed — with several items open at once the totals would otherwise only
              exist inside whichever panel happens to be expanded. Nothing is said until a
              quantity is entered: an empty panel has no total to report. */}
          {(later > 0 || !!totalError) && (
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              {!!totalError && (
                <ErrorOutlineIcon sx={{ mr: 0.5, fontSize: 18, color: 'error.main !important' }} />
              )}
              <Typography
                sx={{ fontSize: '0.85em', color: totalError ? 'error.main' : mutedLabel }}
              >
                {headerTotals}
              </Typography>
            </Box>
          )}
        </Box>
      </AccordionSummary>

      {/* The header carries the 5px accent bar, so its text starts 5px further in than the
          default padding would put this content — matched here so the two share a left edge. */}
      <AccordionDetails sx={{ pt: 2, pl: '19px' }}>
        {/* Nothing above the split rows now: the header carries everything that describes
            the item, and the order id is stated once at the top of the dialog rather than
            repeated in every panel. */}
        <Box display='flex' alignItems='center'>
          <Typography variant='subtitle1' fontWeight={500}>
            Quantity to fill later
          </Typography>
          {/* Sits with the heading rather than under the rows: it acts on this item's whole
              set, and the footer banner carries the running totals. */}
          <Button
            variant='outlined'
            size='small'
            startIcon={<AddIcon />}
            disabled={noQuantityLeft}
            onClick={onAddSplit}
            sx={{ ml: 'auto' }}
          >
            Split more
          </Button>
        </Box>
        {/* One box for this item's whole set — extra splits are appended inside it rather
            than each sitting in its own panel. */}
        <Box
          sx={{
            mt: 1,
            mb: 1,
            backgroundColor: 'background.paper',
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {splits.map((split, rowIndex) => {
            const quantity = toNumber(split?.quantity);
            const allocatedElsewhere = later - (Number.isNaN(quantity) ? 0 : quantity);
            // The arrows stop where this row would leave nothing to fill now.
            const maxForRow = Math.max(0, dispenseQuantity - allocatedElsewhere - 1);
            const idPrefix = isSingleItem
              ? `SplitOrderByQuantity`
              : `SplitOrderByQuantity-${index}`;

            return (
              <Grid
                container
                // form.change adds the row before setRowKeys flushes, so the new row has no
                // key for one render.
                key={`split-${rowKeys?.[rowIndex] ?? rowIndex}`}
                spacing={1}
                alignItems='center'
                // No rule between rows: the Split N labels and the fields' own outlines
                // already separate them, so the set reads as one box rather than a stack
                // of banded rows.
                sx={{ px: 1.5, py: 1 }}
              >
                {splits.length > 1 && (
                  <Grid item xs={12} sm='auto'>
                    <Typography sx={{ fontWeight: 500, fontSize: '0.85em' }}>
                      {`Split ${rowIndex + 1}`}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12} sm={5}>
                  {/* A number input with its native up/down arrows, like the other quantity
                      fields in the portal. The theme suppresses those arrows globally, so
                      they are re-enabled for this field. */}
                  <Box sx={NUMBER_SPINNER_SX}>
                    <TextField
                      id={`${idPrefix}-quantity-${rowIndex}-input`}
                      label='Quantity'
                      name={`${fieldPrefix}[${rowIndex}].quantity`}
                      type='number'
                      required
                      validations={[validateQuantity('Quantity must be greater than 0')]}
                      parse={parseQuantity}
                      params={{
                        inputProps: {
                          min: 0,
                          max: maxForRow,
                          step: 1,
                          inputMode: 'numeric',
                          onKeyDown: (event) => {
                            if (isBlockedQuantityKey(event.key)) event.preventDefault();
                          },
                          onPaste: (event) => {
                            const pasted = (event.clipboardData?.getData('text') ?? '').trim();

                            if (pasted !== parseQuantity(pasted)) event.preventDefault();
                          },
                        },
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} sm>
                  <DateField
                    id={`${idPrefix}-fillDate-${rowIndex}-input`}
                    label='Fill date'
                    name={`${fieldPrefix}[${rowIndex}].fillDate`}
                    required
                    // validateMinDate is a strict comparison, so the bound handed to
                    // DateField sits one day before the allowed window; shouldDisableDate
                    // greys out that boundary day, which the picker would otherwise treat
                    // as inclusive and leave clickable.
                    minDate={subDays(earliestSelectableDate, 1)}
                    // A year out is as far as a fill is worth scheduling. validateMaxDate is
                    // strict in the same way validateMinDate is, so the bound handed over sits
                    // one day past the allowed window and shouldDisableDate greys out that
                    // extra day — otherwise the last allowed day would itself be rejected.
                    maxDate={addDays(latestSelectableDate, 1)}
                    validations={[validateFillDate()]}
                    shouldDisableDate={(date) =>
                      startOfDay(date) < earliestSelectableDate ||
                      startOfDay(date) > latestSelectableDate
                    }
                    // No helper text: the calendar already greys out everything outside the
                    // window, and the validation message says the rule when a typed date
                    // breaks it.
                  />
                </Grid>

                {splits.length > 1 && (
                  <Grid item xs={12} sm='auto'>
                    <IconButton
                      aria-label={`Remove split ${rowIndex + 1}`}
                      size='small'
                      color='error'
                      onClick={() => onRemoveSplit(rowIndex)}
                    >
                      <CloseIcon fontSize='small' />
                    </IconButton>
                  </Grid>
                )}
              </Grid>
            );
          })}
        </Box>

        {/* One place for anything about this item's splits as a whole, so it does not read
            as a complaint about whichever row happens to be first. */}
        {!!totalError && <Alert severity='error'>{totalError}</Alert>}

        <Box sx={{ mt: 2 }}>
          <FillLaterAddressField
            id={`SplitOrderByQuantity-address-${lineItem?.lineItemId}`}
            addresses={addresses}
            value={addressDirectoryId}
            onChange={onAddressChange}
            onBlockedChange={onAddressBlockedChange}
            onAcknowledgedChange={onAddressAcknowledgedChange}
            inheritedAddress={orderAddress}
            loading={addressesLoading}
          />
          <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 0.5 }}>
            Applies to every date above.
          </Typography>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

SplitOrderByQuantityItemPanel.propTypes = {
  lineItem: PropTypes.shape({
    lineItemId: PropTypes.string,
    itemName: PropTypes.string,
    strength: PropTypes.string,
    form: PropTypes.string,
    ndc: PropTypes.string,
    rxNumber: PropTypes.string,
    quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    prescribedQuantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  index: PropTypes.number.isRequired,
  splits: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  rowKeys: PropTypes.arrayOf(PropTypes.number),
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onAddSplit: PropTypes.func.isRequired,
  onRemoveSplit: PropTypes.func.isRequired,
  totalError: PropTypes.string,
  earliestSelectableDate: PropTypes.instanceOf(Date).isRequired,
  latestSelectableDate: PropTypes.instanceOf(Date).isRequired,
  validateFillDate: PropTypes.func.isRequired,
  isSingleItem: PropTypes.bool,
  addresses: PropTypes.arrayOf(PropTypes.shape({})),
  orderAddress: PropTypes.shape({}),
  addressesLoading: PropTypes.bool,
  addressDirectoryId: PropTypes.string,
  onAddressChange: PropTypes.func,
  onAddressBlockedChange: PropTypes.func,
  onAddressAcknowledgedChange: PropTypes.func,
};

SplitOrderByQuantityItemPanel.defaultProps = {
  onAddressAcknowledgedChange: undefined,
  rowKeys: undefined,
  totalError: undefined,
  isSingleItem: false,
  addresses: [],
  orderAddress: null,
  addressesLoading: false,
  addressDirectoryId: null,
  onAddressChange: () => {},
  onAddressBlockedChange: () => {},
};

export default SplitOrderByQuantityItemPanel;
